import { podzielNaRzedy } from "@gierki/shared";

/**
 * Klawiatura ekranowa Wordle'a.
 *
 * Natywna klawiatura systemowa jest **wyłączona** i tu jej zamiennik. Trzy
 * powody, z których każdy sam wystarcza: zajmuje pół ekranu, podpowiada słowa
 * i potrafi wstawić znak spoza alfabetu.
 *
 * Podziału na rzędy **nie zaszywamy trzy razy**. Komponent dostaje tablicę
 * liter i dzieli ją `podzielNaRzedy` tak, żeby żaden rząd nie przekroczył
 * ośmiu klawiszy — 23 → 8/8/7, 26 → 7/7/6/6, 27 → 7/7/7/6. Trzy układy wpisane
 * ręcznie to trzy miejsca do poprawienia przy pierwszym czwartym języku i trzy
 * okazje, żeby o jednym zapomnieć. Rząd dziewięciu klawiszy dałby szerokość
 * 39 px, czyli poniżej progu trafialności — dlatego alfabet 26- i 27-literowy
 * dostaje czwarty rząd, a nie węższy klawisz.
 *
 * ## Klawisz „dodaj ogonek"
 *
 * W partii z ogonkami dochodzi **jeden** klawisz, i to nie do rzędu literowego,
 * tylko do funkcyjnego — między `⌫` a `ENTER`. Rachunek jest w komentarzu przy
 * `WARIANTY_OGONKA` w `shared`, a jego wynik jest tu jednozdaniowy: rzędy
 * literowe zostają dokładnie takie, jakie były (8/8/7 i 7/7/7/6, klawisz
 * 44,6 px), bo pełny alfabet dołożyłby polskiemu czwarty rząd, a hiszpańskiemu
 * piąty — 366 px klawiatury, która nad siatką sześciu kafelków przy ośmiu
 * próbach po prostu nie mieści się na ekranie.
 *
 * Trzy klawisze funkcyjne na 378 px to 124 px każdy; `⌫` i `ENTER` zostają po
 * przeciwnych stronach, więc kciuk nadal nie skasuje litery zamiast wysłać
 * słowo.
 *
 * Klawisz **pokazuje literę, którą wstawi** (po `l` widnieje na nim `ł`), więc
 * nikt nie musi pamiętać, gdzie w cyklu `z → ź → ż` się znajduje. Bez ostatniej
 * litery albo bez wariantu jest wyłączony.
 */

export type KolorLitery = "trafiona" | "obecna" | "nieobecna";

const TLO: Record<KolorLitery, string> = {
  trafiona: "var(--wordle-trafiona)",
  obecna: "var(--wordle-obecna)",
  nieobecna: "var(--wordle-nieobecna)",
};

export function Klawiatura({
  litery,
  kolory,
  onLitera,
  onKasuj,
  onZatwierdz,
  onOgonek,
  podgladOgonka,
  zablokowana,
}: {
  litery: string[];
  /** Najlepszy kolor, jaki litera dostała **w moich** próbach. */
  kolory: Record<string, KolorLitery>;
  onLitera: (litera: string) => void;
  onKasuj: () => void;
  onZatwierdz: () => void;
  /** Podany tylko w partii z ogonkami; bez niego klawisza w ogóle nie ma. */
  onOgonek?: () => void;
  /** Litera, którą klawisz ogonka wstawi teraz; `null` = nie ma czego zamienić. */
  podgladOgonka?: string | null;
  zablokowana: boolean;
}) {
  const rzedy = podzielNaRzedy(litery);

  return (
    <div className="space-y-1.5">
      {rzedy.map((rzad, indeks) => (
        <div key={indeks} className="flex justify-center gap-1.5">
          {rzad.map((litera) => {
            const kolor = kolory[litera];
            return (
              <button
                key={litera}
                type="button"
                disabled={zablokowana}
                onClick={() => onLitera(litera)}
                className={
                  "flex-1 min-h-14 rounded-lg text-lg font-semibold uppercase transition-colors " +
                  "disabled:opacity-40 disabled:pointer-events-none " +
                  (kolor === undefined ? "bg-panel-jasny text-tekst" : "")
                }
                style={
                  kolor === undefined
                    ? undefined
                    : { backgroundColor: TLO[kolor], color: "var(--wordle-napis)" }
                }
              >
                {litera}
              </button>
            );
          })}
        </div>
      ))}

      {/*
        * `ENTER` i `⌫` są szersze, bo to one są dotykane w pośpiechu — i stoją
        * po przeciwnych stronach, żeby kciuk nie kasował litery zamiast wysłać
        * słowo. Klawisz ogonka wchodzi **między nie**, a nie obok jednego
        * z nich: gdyby stanął z brzegu, para „kasuj / wyślij" straciłaby
        * rozdzielenie, po które ją tak ustawiono.
        */}
      <div className="flex justify-center gap-1.5">
        <button
          type="button"
          disabled={zablokowana}
          onClick={onKasuj}
          aria-label="Skasuj literę"
          className="flex-[3] min-h-14 rounded-lg bg-panel-jasny text-tekst text-lg disabled:opacity-40 disabled:pointer-events-none"
        >
          ⌫
        </button>
        {onOgonek !== undefined ? (
          <button
            type="button"
            /*
             * Wyłączony, gdy nie ma czego zamienić — zamiast po cichu nic nie
             * robić. Klawisz, który po dotknięciu nie robi nic, jest w aucie
             * gorszy niż jego brak: gracz dotyka go drugi i trzeci raz.
             */
            disabled={zablokowana || !podgladOgonka}
            onClick={onOgonek}
            aria-label={
              podgladOgonka
                ? `Zamień ostatnią literę na ${podgladOgonka}`
                : "Dodaj ogonek do ostatniej litery"
            }
            className={
              "flex-[3] min-h-14 rounded-lg bg-panel-jasny text-tekst text-lg font-semibold " +
              "uppercase disabled:opacity-40 disabled:pointer-events-none"
            }
          >
            {/* Podgląd wyniku, nie nazwa funkcji: po `l` widnieje tu `ł`. */}
            {podgladOgonka ?? "ą"}
          </button>
        ) : null}
        <button
          type="button"
          disabled={zablokowana}
          onClick={onZatwierdz}
          className="flex-[3] min-h-14 rounded-lg bg-akcent text-tlo text-lg font-semibold disabled:opacity-40 disabled:pointer-events-none"
        >
          ENTER
        </button>
      </div>
    </div>
  );
}

/** Trafiona bije obecną, obecna bije nieobecną. */
const RANGA: Record<KolorLitery, number> = { nieobecna: 1, obecna: 2, trafiona: 3 };

/**
 * Kolory klawiszy z **własnych** prób.
 *
 * Liczy je klient, bo serwer nie ma tu czego dosyłać: gracz zna swoje słowa
 * i swoje kolory, a każde dodatkowe pole w widoku to kolejne miejsce na wyciek.
 */
export function koloryKlawiszy(
  proby: ReadonlyArray<{ slowo: string; kolory: string[] }>,
): Record<string, KolorLitery> {
  const wynik: Record<string, KolorLitery> = {};
  for (const proba of proby) {
    [...proba.slowo].forEach((litera, i) => {
      const kolor = proba.kolory[i] as KolorLitery | undefined;
      if (kolor === undefined) return;
      const teraz = wynik[litera];
      if (teraz === undefined || RANGA[kolor] > RANGA[teraz]) wynik[litera] = kolor;
    });
  }
  return wynik;
}
